import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../../../config.json'; // Ensure your config has the right structure
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, CalendarDays, Youtube, Share2 } from 'lucide-react';

export default function YouTubeViewer() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

    const theme = {
    navy: '#0a192f', // Dark blue/black
    gold: '#c5a059', // Metallic gold/beige
    lightBg: '#fdfaf5', // Soft off-white
    white: '#ffffff',
    textDark: '#333333',
    textMuted: '#6c757d'
  };

  useEffect(() => {
    fetchAllVideos();
  }, []);

  const fetchAllVideos = async () => {
    try {
      setLoading(true);

      const API_KEY = config.youtubeapi.apiKey;
      
      const channels = [
        { name: "MduSchoolEd", id: config.youtubeapi.mduednChannelId },
        { name: "StJosephTrichy", id: config.youtubeapi.stJosephTrichyChannelId },
        { name: "StXavierThoothuKudi", id: config.youtubeapi.stXavierThoothuKudiChannelId }
      ];

      // 1. Get Uploads Playlist IDs
      const channelRequests = channels.map(channel => 
        axios.get(`https://www.googleapis.com/youtube/v3/channels`, {
          params: { key: API_KEY, id: channel.id, part: "contentDetails" }
        })
      );

      console.log("Fetching channel details...",channelRequests);
      
      const channelResponses = await Promise.all(channelRequests);
      
      const playlistIds = channelResponses.map(res => 
        res.data.items[0].contentDetails.relatedPlaylists.uploads
      );

      // 2. Fetch videos
      const videoRequests = playlistIds.map(playlistId => 
        axios.get(`https://www.googleapis.com/youtube/v3/playlistItems`, {
          params: {
            key: API_KEY,
            playlistId: playlistId,
            part: "snippet",
            maxResults: 6, // 6 from each = 12 total
          }
        })
      );

      const videoResponses = await Promise.all(videoRequests);

      // 3. Flatten, map, format date, and sort
      let combinedVideos = [];
      videoResponses.forEach(res => {
        const formatted = res.data.items.map(item => {
            const snippet = item.snippet;
            
            // Format date: e.g., "15 Oct 2023"
            const date = new Date(snippet.publishedAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });

            return {
                id: snippet.resourceId.videoId,
                youtubeId: snippet.resourceId.videoId,
                title: snippet.title,
                publishedAt: new Date(snippet.publishedAt),
                displayDate: date,
                thumbnail: snippet.thumbnails.maxres?.url || 
                           snippet.thumbnails.high?.url || 
                           snippet.thumbnails.default?.url,
                channelTitle: snippet.channelTitle
            };
        });
        combinedVideos = [...combinedVideos, ...formatted];
      });

      combinedVideos.sort((a, b) => b.publishedAt - a.publishedAt);
      console.log("Combined and sorted videos:", combinedVideos);
      setVideos(combinedVideos.slice(0, 12));

    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
        <div className="min-vh-100 pb-5" style={{ backgroundColor: theme.lightBg }}>
      
      {/* --- Header Section --- */}

       <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="display-4 fw-bold mb-3"
          >
            <span className="text-uppercase tracking-widest small fw-bold" style={{ color: theme.navy, letterSpacing: '2px' }}>
              Video Gallery
            </span>

          </motion.h1>
         

      {/* --- Video Grid --- */}
      <div className="container py-5">
        {loading ? (
            <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{color: theme.gold}}></div>
                <p className="mt-2" style={{color: theme.navy}}>Loading....</p>
            </div>
        ) : (
            <div className="row g-4">
            {videos.map((video, index) => (
                <motion.div 
                    key={video.id}
                    className="col-12 col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                    <div 
                        className="card h-100 border-0 rounded-3 overflow-hidden shadow-sm transition-all"
                        style={{ cursor: 'pointer', backgroundColor: theme.white }}
                        onClick={() => setActiveVideo(video)}
                    >
                        {/* Thumbnail View */}
                        <div className="position-relative" style={{ aspectRatio: '16/9' }}>
                            <img 
                                src={video.thumbnail} 
                                alt={video.title}
                                loading="lazy"
                                className="w-100 h-100 object-fit-cover"
                            />
                            {/* Play Button Overlay */}
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-25 hover-overlay transition-all">
                                <div className="rounded-circle d-flex align-items-center justify-content-center shadow" style={{ width: '65px', height: '65px', backgroundColor: 'rgba(255,255,255,0.8)' }}>
                                    <Play fill={theme.navy} size={30} className="text-dark" />
                                </div>
                            </div>
                        </div>

                        {/* Video Info */}
                        <div className="p-4 d-flex flex-column">
                            <span className="badge rounded-pill mb-2 align-self-start" style={{backgroundColor: theme.navy, color: theme.gold, fontSize: '0.75rem'}}>
                                {video.channelTitle}
                            </span>
                            
                            <h5 className="fw-bold mb-3 text-dark text-truncate-2" style={{ lineHeight: '1.4', height: '48px' }}>
                                {video.title}
                            </h5>
                            
                            <div className="d-flex align-items-center mt-auto pt-2 border-top" style={{ color: theme.textMuted, fontSize: '0.9rem' }}>
                                <CalendarDays size={16} className="me-2" style={{color: theme.gold}}/>
                                {video.displayDate}
                            </div>
                        </div>
                    </div>
                </motion.div>
            ))}
            </div>
        )}
      </div>

      {/* --- Lightbox Modal --- */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
            style={{ zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.9)' }}
            onClick={() => setActiveVideo(null)}
          >
            <button 
              className="btn position-absolute top-0 end-0 m-4 text-white hover-rotate"
              style={{ zIndex: 10000 }}
              onClick={() => setActiveVideo(null)}
            >
              <X size={40} />
            </button>

            <motion.div 
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="w-100 shadow-lg"
              style={{ maxWidth: '900px' }}
              onClick={e => e.stopPropagation()}
            >
              <div className="ratio ratio-16x9 rounded-3 overflow-hidden border border-light">
                <iframe 
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1&rel=0`} 
                  title="YouTube video player" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
              <div className="mt-3 text-white d-flex justify-content-between align-items-center">
                <h3 className="fw-bold fs-5 me-3">{activeVideo.title}</h3>
                {/*<button className="btn btn-sm rounded-pill px-3" style={{backgroundColor: theme.gold, color: theme.navy}}>
                  <Share2 size={16} className="me-1" /> Share
                </button>*/}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <style dangerouslySetInnerHTML={{ __html: `
        .transition-all { transition: all 0.3s ease; }
        .hover-overlay { opacity: 0; }
        .card:hover .hover-overlay { opacity: 1; }
        .card:hover { transform: translateY(-5px); }
        .hover-rotate:hover { transform: rotate(90deg); transition: 0.3s; }
        .object-fit-cover { object-fit: cover; }
        .text-truncate-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }
      `}} />
    </div>

  );
}
